import { useEffect, useState } from 'react';
import Link from 'next/link';

import { ListingCard } from '@components/article/ListingCard';
import { ALL_ARTICLES_PATH } from '@components/homepage/latest-articles';
import { useLeagueContext } from '@hooks/useLeagueContext';
import { formatTimeUntilDeadline } from '@lib/homepage/countdown';
import type { ThisGameweekCard } from '@lib/homepage/this-gameweek';

const HEADING_ID = 'this-gameweek-heading';
// Minute precision, so a tick twice a minute never shows a stale minute for long.
const COUNTDOWN_TICK_MS = 30 * 1000;

type DeadlineCountdownProps = {
  gameweek: number;
  deadlineAt: string;
};

const DeadlineCountdown = ({
  gameweek,
  deadlineAt
}: DeadlineCountdownProps) => {
  // Null until mounted: the server can't know the visitor's "now", and a
  // server-rendered countdown would be wrong by the time it hydrated.
  const [nowMs, setNowMs] = useState<number | null>(null);

  useEffect(() => {
    setNowMs(Date.now());
    const timer = setInterval(() => setNowMs(Date.now()), COUNTDOWN_TICK_MS);
    return () => clearInterval(timer);
  }, []);

  const timeLeft =
    nowMs === null ? null : formatTimeUntilDeadline(deadlineAt, nowMs);
  if (!timeLeft) return null;

  return (
    <p
      className="inline-flex items-center gap-2 rounded-lg bg-landing-ink px-3.5 py-2 font-open-sans text-sm text-neutral-300"
      data-cy="deadline-countdown"
    >
      {`GW${gameweek} deadline in`}
      <time
        dateTime={deadlineAt}
        className="font-sans text-base font-bold tabular-nums text-white"
      >
        {timeLeft}
      </time>
    </p>
  );
};

type ThisGameweekProps = {
  cards: ThisGameweekCard[];
};

/**
 * The pieces worth reading before the coming deadline. The cards are part of
 * the static page, so crawlers always see this gameweek's links; the
 * countdown fills in once the browser knows the deadline.
 */
const ThisGameweek = ({ cards }: ThisGameweekProps) => {
  const { context } = useLeagueContext();
  const nextGameweek = context?.nextGameweek ?? null;

  if (cards.length === 0) return null;

  const deadlineLabel = nextGameweek
    ? `the GW${nextGameweek.number} deadline`
    : 'the next FPL deadline';

  return (
    <section aria-labelledby={HEADING_ID} className="bg-landing-surface">
      <div className="mx-auto max-w-7xl px-4 py-12 md:px-8 lg:py-16">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2
              id={HEADING_ID}
              className="font-sans text-2xl font-bold leading-8 text-landing-ink lg:text-[28px] lg:leading-9"
            >
              This gameweek
            </h2>
            <p className="mt-1 font-open-sans text-sm text-landing-slate">
              {`Everything you need before ${deadlineLabel}.`}
            </p>
          </div>
          <div className="min-h-[40px]">
            {nextGameweek && (
              <DeadlineCountdown
                gameweek={nextGameweek.number}
                deadlineAt={nextGameweek.deadlineAt}
              />
            )}
          </div>
        </div>
        <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-3 lg:gap-6">
          {cards.map(({ slot, item, label }) => (
            <ListingCard
              key={slot}
              item={item}
              label={label}
              className="rounded-xl bg-white p-3 lg:p-0"
            />
          ))}
        </div>
        <Link
          href={ALL_ARTICLES_PATH}
          className="mt-6 inline-flex items-center gap-2 font-sans text-[15px] font-semibold text-primary-700 transition-colors hover:text-primary-800"
        >
          All FPL articles
          <span
            aria-hidden="true"
            className="inline-block h-[7px] w-[7px] rotate-45 border-t-2 border-r-2 border-current"
          />
        </Link>
      </div>
    </section>
  );
};

export default ThisGameweek;
